import { z } from 'zod'

/**
 * **Was der volle Todoist-Sync liefert** — nur die Felder, die der Import
 * braucht, mit Todoists Namen. Die Übersetzung in HappyTask-Begriffe macht
 * der Import (T07); die Feldkarte steht in `tools/todoist-api-map/index.html`.
 *
 * Großzügig mit Vorgaben: Fehlt ein Feld, das nur Beiwerk ist, bleibt die
 * Zeile trotzdem brauchbar. Unbekannte Felder streicht Zod.
 */

const DueZ = z.object({
  date: z.string(),
  timezone: z.string().nullish(),
  string: z.string().nullish(),
  lang: z.string().nullish(),
  is_recurring: z.boolean().default(false),
})

const DeadlineZ = z.object({
  date: z.string(),
  lang: z.string().nullish(),
})

const DurationZ = z.object({
  amount: z.number(),
  unit: z.enum(['minute', 'day']),
})

const AttachmentZ = z.object({
  file_name: z.string().nullish(),
  file_url: z.string().nullish(),
  resource_type: z.string().nullish(),
})

export const TodoistProjectZ = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullish(),
  color: z.string().nullish(),
  parent_id: z.string().nullish(),
  child_order: z.number().default(0),
  is_archived: z.boolean().default(false),
  is_favorite: z.boolean().default(false),
  inbox_project: z.boolean().default(false),
  is_deleted: z.boolean().default(false),
})

export const TodoistSectionZ = z.object({
  id: z.string(),
  name: z.string(),
  project_id: z.string(),
  section_order: z.number().default(0),
  is_collapsed: z.boolean().default(false),
  description: z.string().nullish(),
  is_archived: z.boolean().default(false),
  /** Seit wann archiviert (#193); nur an Abschnitten aus `GET /sections/{id}`. */
  archived_at: z.string().nullish(),
  is_deleted: z.boolean().default(false),
})

export const TodoistItemZ = z.object({
  id: z.string(),
  project_id: z.string(),
  section_id: z.string().nullish(),
  parent_id: z.string().nullish(),
  content: z.string(),
  description: z.string().nullish(),
  due: DueZ.nullish(),
  deadline: DeadlineZ.nullish(),
  duration: DurationZ.nullish(),
  priority: z.number().int().min(1).max(4).default(1),
  labels: z.array(z.string()).default([]),
  checked: z.boolean().default(false),
  completed_at: z.string().nullish(),
  added_at: z.string().nullish(),
  updated_at: z.string().nullish(),
  child_order: z.number().default(0),
  day_order: z.number().nullish(),
  is_collapsed: z.boolean().default(false),
  is_deleted: z.boolean().default(false),
  /** Wie oft erledigt bzw. das Datum geändert (#198) — fehlt in älteren Antworten. */
  completed_count: z.number().int().nonnegative().nullish(),
  postponed_count: z.number().int().nonnegative().nullish(),
})

/** Eine erledigte Aufgabe: ohne Zeitpunkt des Erledigens ist sie für den Verlauf wertlos. */
export const CompletedItemZ = TodoistItemZ.extend({ completed_at: z.string() })

export const TodoistLabelZ = z.object({
  id: z.string(),
  name: z.string(),
  color: z.string().nullish(),
  item_order: z.number().nullish(),
  is_favorite: z.boolean().default(false),
  is_deleted: z.boolean().default(false),
})

export const TodoistNoteZ = z.object({
  id: z.string(),
  item_id: z.string(),
  content: z.string().default(''),
  posted_at: z.string().nullish(),
  file_attachment: AttachmentZ.nullish(),
  is_deleted: z.boolean().default(false),
})

export const TodoistProjectNoteZ = z.object({
  id: z.string(),
  project_id: z.string(),
  content: z.string().default(''),
  posted_at: z.string().nullish(),
  file_attachment: AttachmentZ.nullish(),
  is_deleted: z.boolean().default(false),
})

/**
 * **Eine Erinnerung** (Plan task-reminders, Etappe 6): `relative` = `minute_offset` Minuten vor der
 * Uhrzeit der Aufgabe, `absolute` = ein eigenes `due` (derselbe Typ wie an Aufgaben, mit
 * Wiederholung). Orts-Erinnerungen kommen getrennt (`reminders_location`) und werden nur gezählt.
 * `notify_uid` nennt, wen sie erinnert — in geteilten Projekten auch andere Personen.
 */
export const TodoistReminderZ = z.object({
  id: z.string(),
  item_id: z.string(),
  notify_uid: z.string().nullish(),
  type: z.string(),
  due: DueZ.nullish(),
  minute_offset: z.number().nullish(),
  is_urgent: z.boolean().default(false),
  is_deleted: z.boolean().default(false),
})

/** Nur, um sie zu zählen: HappyTask erinnert nicht an Orten. */
export const TodoistLocationReminderZ = z.object({
  id: z.string(),
  item_id: z.string().nullish(),
  is_deleted: z.boolean().default(false),
})

export type TodoistProject = z.infer<typeof TodoistProjectZ>
export type TodoistSection = z.infer<typeof TodoistSectionZ>
export type TodoistItem = z.infer<typeof TodoistItemZ>
export type TodoistLabel = z.infer<typeof TodoistLabelZ>
export type TodoistNote = z.infer<typeof TodoistNoteZ>
export type TodoistProjectNote = z.infer<typeof TodoistProjectNoteZ>
export type TodoistReminder = z.infer<typeof TodoistReminderZ>

/** Alles Aktive aus einem vollen Sync, gelöschte Zeilen schon entfernt. */
export interface TodoistSnapshot {
  projects: TodoistProject[]
  sections: TodoistSection[]
  items: TodoistItem[]
  labels: TodoistLabel[]
  notes: TodoistNote[]
  projectNotes: TodoistProjectNote[]
  /** Erinnerungen nach Zeit (Plan task-reminders); die an Orten nur als Zahl. */
  reminders: TodoistReminder[]
  locationReminders: number
  /** Das eigene Konto (`user.id`) — Erinnerungen anderer Personen (`notify_uid`) bleiben dort. `null`, wenn Todoist es nicht nennt. */
  userId: string | null
  /**
   * Todoists **automatische Erinnerung** (`user.auto_reminder`, Minuten vorher); `null` = aus. Todoist
   * gibt sie nicht als Erinnerungs-Zeile heraus (gemessen 30.09.2026) — der Import setzt dafür die
   * Standard-Erinnerung.
   */
  autoReminder: number | null
  /** Der Verlauf (T04) — leer, bis er vollständig geholt ist. */
  completedItems: TodoistItem[]
  /** Zeilen, die das Schema nicht bestanden haben — fehlen im Schnappschuss. */
  skipped: number
}

/** Liest eine Liste Zeile für Zeile: Kaputtes fällt raus und wird gezählt, Gelöschtes fällt still raus. */
export function parseRows<T extends { is_deleted: boolean }>(
  schema: z.ZodType<T>,
  raw: unknown,
): { rows: T[]; skipped: number } {
  if (!Array.isArray(raw)) return { rows: [], skipped: 0 }
  const rows: T[] = []
  let skipped = 0
  for (const entry of raw) {
    const parsed = schema.safeParse(entry)
    if (!parsed.success) skipped++
    else if (!parsed.data.is_deleted) rows.push(parsed.data)
  }
  return { rows, skipped }
}

export function parseSnapshot(body: unknown): TodoistSnapshot {
  const data = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>
  const projects = parseRows(TodoistProjectZ, data.projects)
  const sections = parseRows(TodoistSectionZ, data.sections)
  const items = parseRows(TodoistItemZ, data.items)
  const labels = parseRows(TodoistLabelZ, data.labels)
  const notes = parseRows(TodoistNoteZ, data.notes)
  const projectNotes = parseRows(TodoistProjectNoteZ, data.project_notes)
  const reminders = parseRows(TodoistReminderZ, data.reminders)
  const locationReminders = parseRows(TodoistLocationReminderZ, data.reminders_location)
  const user = (data.user && typeof data.user === 'object' ? data.user : {}) as { id?: unknown; auto_reminder?: unknown }
  return {
    projects: projects.rows,
    sections: sections.rows,
    items: items.rows,
    labels: labels.rows,
    notes: notes.rows,
    projectNotes: projectNotes.rows,
    reminders: reminders.rows,
    locationReminders: locationReminders.rows.length,
    userId: typeof user.id === 'string' ? user.id : typeof user.id === 'number' ? String(user.id) : null,
    autoReminder: typeof user.auto_reminder === 'number' && user.auto_reminder >= 0 ? user.auto_reminder : null,
    completedItems: [],
    skipped: projects.skipped + sections.skipped + items.skipped + labels.skipped + notes.skipped + projectNotes.skipped
      + reminders.skipped + locationReminders.skipped,
  }
}

export interface CompletedWindow {
  since: Date
  until: Date
}

/** Todoist erlaubt „bis zu 3 Monate“ je Abruf; 90 Tage lassen Puffer. */
export const COMPLETED_WINDOW_DAYS = 90
const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Zerlegt `since`–`until` in Fenster von höchstens `days` Tagen, das
 * **neueste zuerst**: Bricht der Abruf ab, ist wenigstens der jüngste Verlauf
 * da. Lückenlos, ohne Überlappung (`until` ist bei Todoist exklusiv).
 */
export function completedWindows(since: Date, until: Date, days: number = COMPLETED_WINDOW_DAYS): CompletedWindow[] {
  const windows: CompletedWindow[] = []
  let end = until.getTime()
  const start = since.getTime()
  while (end > start) {
    const from = Math.max(start, end - days * DAY_MS)
    windows.push({ since: new Date(from), until: new Date(end) })
    end = from
  }
  return windows
}
